'use client';

import { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';

interface CalculatorResult {
  recommendedPlan: string;
  monthlyCost: number;
  annualSavings: number;
}

export default function PricingCalculator() {
  const [isHydrated, setIsHydrated] = useState(false);
  const [signalsPerDay, setSignalsPerDay] = useState(10);
  const [tradingDays, setTradingDays] = useState(20);
  const [result, setResult] = useState<CalculatorResult | null>(null);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  useEffect(() => {
    if (!isHydrated) return;
    
    const monthlySignals = signalsPerDay * tradingDays;
    let recommendedPlan = 'Free';
    let monthlyCost = 0;
    let annualSavings = 0;

    if (monthlySignals <= 50) {
      recommendedPlan = 'Free';
      monthlyCost = 0;
    } else if (monthlySignals <= 300) {
      recommendedPlan = 'Pro';
      monthlyCost = 29;
      annualSavings = 348 - 290;
    } else {
      recommendedPlan = 'Premium';
      monthlyCost = 79;
      annualSavings = 948 - 790;
    }

    setResult({ recommendedPlan, monthlyCost, annualSavings });
  }, [signalsPerDay, tradingDays, isHydrated]);

  if (!isHydrated) {
    return (
      <div className="bg-card border border-border rounded-2xl p-8">
        <div className="animate-pulse space-y-4">
          <div className="h-6 bg-muted rounded w-3/4"></div>
          <div className="h-4 bg-muted rounded w-1/2"></div>
          <div className="h-32 bg-muted rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-2xl p-8">
      <div className="flex items-center space-x-3 mb-6">
        <Icon name="CalculatorIcon" size={24} className="text-primary" />
        <h3 className="text-2xl font-heading font-bold text-foreground">Usage Calculator</h3>
      </div>

      <p className="text-sm text-muted-foreground mb-6">
        Find the perfect plan based on your trading activity
      </p>

      <div className="space-y-6">
        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            Signals per day: {signalsPerDay}
          </label>
          <input
            type="range"
            min="1"
            max="50"
            value={signalsPerDay}
            onChange={(e) => setSignalsPerDay(Number(e.target.value))}
            className="w-full h-2 bg-muted rounded-lg appearance-none cursor-pointer accent-primary"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            Trading days per month: {tradingDays}
          </label>
          <input
            type="range"
            min="1"
            max="30"
            value={tradingDays}
            onChange={(e) => setTradingDays(Number(e.target.value))}
            className="w-full h-2 bg-muted rounded-lg appearance-none cursor-pointer accent-primary"
          />
        </div>

        {result && (
          <div className="bg-primary/10 border border-primary rounded-lg p-6 mt-6">
            <div className="flex items-center justify-between mb-4">
              <span className="text-sm text-muted-foreground">Recommended Plan</span>
              <span className="text-2xl font-heading font-bold text-primary">{result.recommendedPlan}</span>
            </div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-muted-foreground">Monthly Cost</span>
              <span className="text-xl font-heading font-semibold text-foreground">${result.monthlyCost}</span>
            </div>
            {result.annualSavings > 0 && (
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Annual Savings</span>
                <span className="text-lg font-heading font-semibold text-success">${result.annualSavings}</span>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}